import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";

import api from "../utilities/api.js";
import { CheckboxGroup } from "./ToolsPage-StandardsImport.jsx";
import { RadioGroup } from "./ToolsPage-Export.jsx";
import {
  standardRefsExportFrameworkOptions,
  standardRefsExportFileFormatOptions,
} from "./ToolsPage-options.js";

// Message of a failed request; blob responses carry the JSON error as text
const errorMessage = async (error) => {
  const data = error.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text()).message || error.message;
    } catch {
      return error.message;
    }
  }
  return data?.message || error.message || "Unknown error";
};

// Export Standard References Section Component (Admin only)
// Exports the standard references of the selected standards/frameworks as
// shown on Admin → Stand.-Refs (incl. draft changes), sorted by OFID and standard
const StandardRefsExportSection = ({ dynamicHeight }) => {
  const [standardOptions, setStandardOptions] = useState([]);
  const [selectedStandards, setSelectedStandards] = useState([]);
  const [selectedFrameworks, setSelectedFrameworks] = useState([]);
  const [fileFormat, setFileFormat] = useState("csv");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get(`/standards`)
      .then((response) =>
        setStandardOptions(
          response.data
            // FundsXML and openfunds are frameworks, not standards
            .filter((s) => !/^fundsxml\b/i.test(s.name) && s.name.toLowerCase() !== "openfunds")
            .map((s) => ({ label: s.name, value: s.name })),
        ),
      )
      .catch((error) => {
        console.error("Error fetching standards:", error);
        toast.error("Failed to load standards");
      });
  }, []);

  const toggle = (setter) => (value) =>
    setter((values) =>
      values.includes(value) ? values.filter((v) => v !== value) : [...values, value],
    );

  const handleExport = async () => {
    if (!selectedStandards.length || !selectedFrameworks.length) {
      toast.error("Please select standards and frameworks.", {
        duration: 5000,
        position: "top-center",
      });
      return;
    }
    setBusy(true);
    try {
      const response = await api.post(
        `/export/standard-refs`,
        { standards: selectedStandards, frameworks: selectedFrameworks, format: fileFormat },
        { responseType: "blob" },
      );

      let filename = `standard_references.${fileFormat}`;
      const disposition = response.headers["content-disposition"];
      if (disposition && disposition.indexOf("filename=") !== -1) {
        filename = disposition.split("filename=")[1].replace(/['"]/g, "").trim();
      }

      const urlBlob = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = urlBlob;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(urlBlob);

      toast.success(
        `Export successful!\n${response.headers["x-exported-count"]} references exported.`,
        { duration: 4000, position: "top-center", className: "whitespace-pre-line" },
      );
    } catch (error) {
      console.error("Error exporting standard references:", error);
      toast.error("Export failed!\n" + (await errorMessage(error)), {
        duration: 6000,
        position: "top-center",
        className: "whitespace-pre-line",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col p-5 lg:min-h-[30rem] border border-gray-100 rounded-lg w-full max-w-xl bg-gradient-to-r from-cyan-700 to-cyan-800 text-white">
      <h2 className="text-2xl font-bold text-gray-100 mb-4">
        Export Standard References
      </h2>

      {/* Standards left; Frameworks and File Format stacked right. The row
          stretches all boxes, so both columns end at the same height */}
      <div className="flex gap-4 mb-6" style={{ minHeight: `${dynamicHeight}px` }}>
        <CheckboxGroup
          title="Standards"
          options={standardOptions}
          selectedValues={selectedStandards}
          onToggle={toggle(setSelectedStandards)}
          className="flex-1"
        />
        <div className="flex-1 flex flex-col gap-4 min-w-0">
          <CheckboxGroup
            title="Frameworks"
            options={standardRefsExportFrameworkOptions}
            selectedValues={selectedFrameworks}
            onToggle={toggle(setSelectedFrameworks)}
            className="flex-none"
          />
          <RadioGroup
            title="File Format"
            name="standardRefsExportFormat"
            options={standardRefsExportFileFormatOptions}
            selectedValue={fileFormat}
            onChange={(e) => setFileFormat(e.target.value)}
            className="flex-1"
          />
        </div>
      </div>

      <button
        type="button"
        onClick={handleExport}
        disabled={busy}
        className="w-full mt-auto bg-cyan-600 text-white hover:bg-gray-200 hover:text-gray-800 disabled:opacity-50 font-semibold py-3 px-6 border border-gray-300 rounded-md shadow-md transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-gray-200 focus:ring-opacity-75"
      >
        Export Standard References
      </button>
    </div>
  );
};

StandardRefsExportSection.propTypes = {
  dynamicHeight: PropTypes.number.isRequired,
};

export { StandardRefsExportSection };
